import { lazy } from 'react';
import type { RouteObject } from 'react-router';
import { PublicOnly, RequireAuth } from '@/auth/guards';
import { AppShell } from '@/components/AppShell';
import { RouteError } from '@/components/ErrorBoundary';
import { MOCK } from '@/config';
import { AlertForm } from '@/features/alerts/AlertForm';
import { AlertsPage } from '@/features/alerts/AlertsPage';
import { ForgotPassword } from '@/features/auth/ForgotPassword';
import { ResetPassword } from '@/features/auth/ResetPassword';
import { SignIn } from '@/features/auth/SignIn';
import { SignUp } from '@/features/auth/SignUp';
import { AccountPicker } from '@/features/connect/AccountPicker';
import { ConnectCallback } from '@/features/connect/ConnectCallback';
import { ConnectionsPage } from '@/features/connections/ConnectionsPage';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { DestinationsPage } from '@/features/destinations/DestinationsPage';
import { IntegrationDetail } from '@/features/integrations/IntegrationDetail';
import { Library } from '@/features/integrations/Library';
import { NotFound } from '@/features/NotFound';
import { Onboarding } from '@/features/onboarding/Onboarding';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { ReportWizard } from '@/features/reports/ReportWizard';
import { SettingsPage } from '@/features/settings/SettingsPage';

// Split off the heavier, less visited screens (Recharts lives with the KPI detail).
const KpiDetail = lazy(() => import('@/features/detail/KpiDetail').then((m) => ({ default: m.KpiDetail })));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const MockConsent = lazy(() => import('@/features/connect/MockConsent'));

export const routes: RouteObject[] = [
  {
    path: '/',
    errorElement: <RouteError />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          { path: 'sign-in', element: <SignIn /> },
          { path: 'sign-up', element: <SignUp /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
        ],
      },
      { path: 'reset-password', element: <ResetPassword /> },
      ...(MOCK ? [{ path: '__mock/oauth', element: <MockConsent /> }] : []),
      {
        element: <RequireAuth />,
        children: [
          { path: 'onboarding', element: <Onboarding /> },
          { path: 'connections/callback', element: <ConnectCallback /> },
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Dashboard /> },
              { path: 'kpi/:widgetId', element: <KpiDetail /> },
              { path: 'integrations', element: <Library /> },
              { path: 'integrations/:integrationId', element: <IntegrationDetail /> },
              { path: 'connections', element: <ConnectionsPage /> },
              { path: 'connections/:connectionId/account', element: <AccountPicker /> },
              { path: 'updates', element: <ReportsPage /> },
              { path: 'updates/new', element: <ReportWizard /> },
              { path: 'updates/:reportId', element: <ReportWizard /> },
              { path: 'alerts', element: <AlertsPage /> },
              { path: 'alerts/new', element: <AlertForm /> },
              { path: 'alerts/:alertId', element: <AlertForm /> },
              { path: 'destinations', element: <DestinationsPage /> },
              { path: 'settings', element: <SettingsPage /> },
              { path: 'admin', element: <AdminPage /> },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];
